/**
 * A loja de funcionalidades (#560): o que a organização pode adicionar, num
 * catálogo só.
 *
 * ── Por que um modelo à parte da tela ─────────────────────────────────────
 *
 * Os itens vêm de três leituras que não se conhecem — as funcionalidades
 * (`/v1/admin/flags`), o catálogo de portais e os canais conectados —, e cada
 * uma tem a sua regra de estado. Juntar as três aqui, numa função pura, é o
 * que deixa a regra ser testada sem montar a tela, e deixa a tela só desenhar.
 *
 * Os sistemas de gestão (#855) são a quarta fonte, e a única sem leitura: uma
 * lista fixa, toda "Em breve", para o cliente saber que a integração vem.
 *
 * ── O que NÃO entra ───────────────────────────────────────────────────────
 *
 * Preço e contratação. A issue deixa em aberto se item pago entra na loja, e
 * regra comercial não se decide no código (§21): por ora, só entra o que já
 * está incluído no plano.
 */

import type { FlagDoTenant, Plataforma, TipoDeCanal } from '@cliqcrm/contracts';
import type { NomeDoIcone } from '@/shared/ui';
import { CATALOGO_DE_CANAIS, disponibilidadeDoCanal, EM_BREVE } from '../ui/canais';

export type CategoriaDaLoja = 'canais' | 'portais' | 'erp' | 'crm' | 'ia';

/** Na ordem em que aparecem nos filtros e na vitrine. */
export const CATEGORIAS_DA_LOJA: readonly { chave: CategoriaDaLoja; nome: string }[] = [
  { chave: 'canais', nome: 'Canais de atendimento' },
  { chave: 'portais', nome: 'Portais e anúncios' },
  { chave: 'erp', nome: 'Sistemas de gestão (ERP)' },
  { chave: 'crm', nome: 'Recursos do CRM' },
  { chave: 'ia', nome: 'IA' },
];

export type EstadoNaLoja = 'adicionada' | 'disponivel' | 'em-breve' | 'indisponivel';

export const ROTULO_DO_ESTADO: Readonly<Record<EstadoNaLoja, string>> = {
  adicionada: 'Adicionada',
  disponivel: 'Disponível',
  'em-breve': 'Em breve',
  indisponivel: 'Indisponível',
};

/**
 * O que "Adicionar" faz.
 *
 * Canal tem fluxo próprio — conta na Meta, código no site, quem atende —, e
 * esse fluxo já existe em Conexões: a loja leva até ele, em vez de ter um
 * segundo jeito de conectar. Funcionalidade e portal são uma chave por
 * organização, e ligar é homologar (princípio 08).
 *
 * `nenhuma` é o item que só se mostra, como um sistema de gestão sem
 * integração construída: não há chave nem canal por trás dele, e o tipo é o
 * que impede alguém de pôr ali uma chave que não faz nada.
 */
export type AcaoDaLoja =
  | { tipo: 'canal'; canal: TipoDeCanal }
  | { tipo: 'flag'; chave: string; permanente: boolean }
  | { tipo: 'nenhuma' };

/**
 * A cor da faixa de uma logo cuja marca pede fundo próprio: a do Mercado Livre
 * é amarela, e a do Omie e a do Usados.BR são claras e somem no fundo claro.
 * Sem ela, a faixa da logo é clara e neutra.
 */
export type FundoDaLogo = 'mercadolivre' | 'omie' | 'usadosbr';

/**
 * O glifo do conjunto de ícones, ou a logo de um sistema de fora — portal ou
 * sistema de gestão —, servida de `public/marca/apps/`.
 */
export type IconeDoItem =
  { canal: TipoDeCanal } | { nome: NomeDoIcone } | { imagem: string; fundo?: FundoDaLogo };

export interface ItemDaLoja {
  /** Único na loja inteira: `canal:whatsapp`, `flag:push_com_conteudo`. */
  readonly id: string;
  readonly categoria: CategoriaDaLoja;
  readonly nome: string;
  /** Uma linha, para o cartão. */
  readonly linha: string;
  /** O que faz, por extenso, para o detalhe. */
  readonly sobre: string;
  /**
   * Exemplos do que vai dar para fazer, quando o "o que faz" é genérico demais
   * para a pessoa reconhecer o próprio uso — o de um sistema de gestão vale
   * para todos eles, e é o exemplo que diz "nota fiscal" ou "estoque".
   */
  readonly exemplos?: readonly string[];
  /** O que precisa para funcionar. Vazio quando não precisa de nada. */
  readonly requisitos: readonly string[];
  readonly estado: EstadoNaLoja;
  /**
   * Por que não se adiciona por aqui, quando não se adiciona.
   *
   * Num canal JÁ adicionado ele continua aqui: um TikTok conectado direto no
   * motor está "Adicionado", e ainda assim não se adiciona OUTRO pela loja — o
   * assistente de Conexões não tem o passo dele.
   */
  readonly motivo?: string;
  readonly icone: IconeDoItem;
  readonly acao: AcaoDaLoja;
  /**
   * A chave técnica, no detalhe, para o suporte — nunca em destaque. É o nome
   * como a API o conhece: a chave da funcionalidade ou do portal, o tipo do
   * canal.
   */
  readonly referencia: string;
}

// ── Canais ──────────────────────────────────────────────────────────────────

/** O que cada canal pede de quem vai conectá-lo. */
const REQUISITOS_DO_CANAL: Partial<Record<TipoDeCanal, readonly string[]>> = {
  whatsapp: ['Uma conta do WhatsApp Business na Meta.', 'O número que vai atender.'],
  instagram: [
    'Uma conta profissional do Instagram.',
    'A conta ligada a uma página do Facebook que você administra.',
  ],
  facebook: ['Uma página do Facebook que você administra.'],
  web_chat: ['Acesso ao site da empresa para colar um trecho de código.'],
  api: ['Um sistema da empresa que envie e receba as mensagens por API.'],
  tiktok: ['Uma conta comercial do TikTok.'],
};

function itensDeCanais(
  conectados: readonly { tipo: TipoDeCanal }[],
  whatsappConfigurado: boolean,
): ItemDaLoja[] {
  const tiposConectados = new Set(conectados.map((c) => c.tipo));

  return CATALOGO_DE_CANAIS.map((canal) => {
    const motivo = disponibilidadeDoCanal(canal.tipo, whatsappConfigurado);
    const estado: EstadoNaLoja = tiposConectados.has(canal.tipo)
      ? 'adicionada'
      : motivo === null
        ? 'disponivel'
        : motivo === EM_BREVE
          ? 'em-breve'
          : 'indisponivel';

    return {
      id: `canal:${canal.tipo}`,
      categoria: 'canais',
      nome: canal.nome,
      linha: canal.descricao,
      sobre: `${canal.descricao} As conversas que chegam por ele entram no Atendimento, junto com as dos outros canais.`,
      requisitos: REQUISITOS_DO_CANAL[canal.tipo] ?? [],
      estado,
      ...(motivo !== null ? { motivo } : {}),
      icone: { canal: canal.tipo },
      acao: { tipo: 'canal', canal: canal.tipo },
      referencia: canal.tipo,
    };
  });
}

// ── Portais ─────────────────────────────────────────────────────────────────

/**
 * Os portais que só fazem sentido num segmento.
 *
 * A organização ainda não tem segmento no cadastro (decisão de 01/10/2026,
 * #560): até ter, o portal de um segmento só aparece na loja para quem já o
 * tem ligado. Os gerais aparecem para todos. Quando o segmento existir, esta
 * lista vira um campo do catálogo e o filtro passa a ser por ele.
 */
const PORTAIS_DE_SEGMENTO: ReadonlySet<string> = new Set(['usadosbr']);

/**
 * A logo de cada portal, pelo `provider` do catálogo.
 *
 * Aqui, e não no catálogo da API: a logo é arquivo da tela, e o catálogo não
 * sabe onde a tela guarda as imagens. O portal que não está na lista — um
 * conector novo — aparece com o glifo de portal, e nunca com imagem quebrada.
 */
const LOGO_DO_PORTAL: Readonly<Record<string, IconeDoItem>> = {
  mercadolivre: { imagem: '/marca/apps/mercadolivre.svg', fundo: 'mercadolivre' },
  olx_chat: { imagem: '/marca/apps/olx.svg' },
  olx_lead: { imagem: '/marca/apps/olx.svg' },
  rdstation: { imagem: '/marca/apps/rdstation.svg' },
  usadosbr: { imagem: '/marca/apps/usadosbr.png', fundo: 'usadosbr' },
};

/**
 * "do portal", e não "do OLX Chat": o nome vem do catálogo, e o artigo certo
 * ("da OLX", "do RD Station") não vem junto. O cartão já mostra o nome.
 */
function linhaDoPortal(portal: Plataforma): string {
  const leads = portal.capacidades.includes('lead');
  const conversas = portal.capacidades.includes('chat') || portal.capacidades.includes('mensagem');
  const oQue =
    leads && conversas
      ? 'os interessados e as conversas'
      : conversas
        ? 'as conversas'
        : 'os interessados';
  return `Traz para o CRM ${oQue} do portal.`;
}

function itensDePortais(plataformas: readonly Plataforma[]): ItemDaLoja[] {
  return plataformas
    .filter((p) => p.ativa_no_tenant || !PORTAIS_DE_SEGMENTO.has(p.provider))
    .map((portal) => {
      /*
       * `fichado` é saber o que o portal exige sem ter código que fale com
       * ele. Na loja isso é "Em breve" — oferecer "Adicionar" ligaria uma chave
       * que não faz nada, e a organização esperaria contatos que nunca chegam.
       */
      const estado: EstadoNaLoja = portal.ativa_no_tenant
        ? 'adicionada'
        : portal.situacao === 'fichado'
          ? 'em-breve'
          : 'disponivel';

      // Só o que a ORGANIZAÇÃO fornece: o escopo `aplicacao` é credencial
      // da Cliqfy, e listá-lo aqui pediria à pessoa algo que ela não tem.
      const daOrganizacao = portal.configuracao
        .filter((c) => c.escopo === 'revenda' && c.obrigatorio)
        .map((c) => c.rotulo);

      const linha = linhaDoPortal(portal);
      return {
        id: `portal:${portal.provider}`,
        categoria: 'portais',
        nome: portal.rotulo,
        linha,
        sobre: `${linha} Cada contato vira um cliente com a origem marcada, e entra no funil como os que chegam pelos canais.`,
        requisitos: ['Uma conta da organização no portal.', ...daOrganizacao],
        estado,
        ...(estado === 'em-breve'
          ? { motivo: 'A ligação com este portal ainda está sendo construída.' }
          : {}),
        icone: LOGO_DO_PORTAL[portal.provider] ?? { nome: 'portal' },
        acao: { tipo: 'flag', chave: portal.feature_flag, permanente: false },
        referencia: portal.feature_flag,
      };
    });
}

// ── Sistemas de gestão (ERP) ────────────────────────────────────────────────

/**
 * Os sistemas de gestão com que o CliqCRM vai se integrar (#855): vitrine, para
 * o cliente saber que a integração vem e o comercial poder citá-la.
 *
 * Uma lista fixa, sem leitura da API, porque não há nada por trás ainda — nem
 * conector, nem chave. Quando a primeira integração for construída, o item dela
 * sai daqui e passa a vir do catálogo de conectores, como os portais.
 */
const ERPS: readonly {
  chave: string;
  nome: string;
  /** As linhas de produto, quando a marca tem mais de um sistema de gestão. */
  produtos?: string;
  exemplos: readonly string[];
  icone: IconeDoItem;
}[] = [
  {
    chave: 'totvs',
    nome: 'TOTVS',
    produtos: 'Para o Protheus, o RM, o Datasul e o Winthor.',
    exemplos: [
      'Cadastrar no ERP o cliente que fechou negócio no CRM.',
      'Gerar o pedido de venda no ERP quando a oportunidade é ganha.',
      'Mostrar no cliente os pedidos e as notas fiscais emitidas.',
      'Consultar a situação financeira (títulos em aberto) antes de fechar uma nova venda.',
    ],
    icone: { imagem: '/marca/apps/totvs.svg' },
  },
  {
    chave: 'sankhya',
    nome: 'Sankhya',
    exemplos: [
      'Sincronizar o cadastro de clientes (parceiros) entre o CRM e o Sankhya.',
      'Criar o pedido no Sankhya a partir da oportunidade ganha.',
      'Consultar preço e estoque de produtos durante o atendimento.',
      'Mostrar no cliente os pedidos e o status de faturamento.',
    ],
    icone: { imagem: '/marca/apps/sankhya.svg' },
  },
  {
    chave: 'sap',
    nome: 'SAP',
    produtos: 'Para o SAP Business One e o SAP S/4HANA.',
    exemplos: [
      'Criar o cliente (parceiro de negócios) no SAP quando a venda é fechada.',
      'Gerar a cotação ou o pedido de venda no SAP a partir da oportunidade.',
      'Trazer para o CRM o status de entrega e de faturamento do pedido.',
      'Consultar limite de crédito e títulos em aberto do cliente.',
    ],
    icone: { imagem: '/marca/apps/sap.svg' },
  },
  {
    chave: 'oracle',
    nome: 'Oracle',
    produtos: 'Para o NetSuite e o Oracle Fusion Cloud ERP.',
    exemplos: [
      'Sincronizar clientes e contatos entre o CRM e o Oracle.',
      'Criar o pedido de venda no Oracle a partir da oportunidade ganha.',
      'Mostrar no cliente as faturas e os pagamentos registrados.',
      'Consultar a disponibilidade de produtos durante o atendimento.',
    ],
    icone: { imagem: '/marca/apps/oracle.svg' },
  },
  {
    chave: 'omie',
    nome: 'Omie',
    exemplos: [
      'Cadastrar o cliente no Omie a partir do CRM.',
      'Gerar o pedido ou a ordem de serviço no Omie quando a venda é fechada.',
      'Emitir a nota fiscal e enviar o boleto ou o link de pagamento pelo WhatsApp.',
      'Avisar no CRM quando o pagamento do cliente é confirmado.',
    ],
    icone: { imagem: '/marca/apps/omie.png', fundo: 'omie' },
  },
];

const SOBRE_DO_ERP =
  'Conecta o CliqCRM ao sistema de gestão da empresa, para que clientes, vendas e informações financeiras andem entre os dois sem digitar de novo.';

export const ERPS_DA_LOJA: readonly ItemDaLoja[] = ERPS.map((erp) => ({
  id: `erp:${erp.chave}`,
  categoria: 'erp',
  nome: erp.nome,
  linha: 'Ligue o CRM ao seu sistema de gestão.',
  sobre: erp.produtos ? `${SOBRE_DO_ERP} ${erp.produtos}` : SOBRE_DO_ERP,
  exemplos: erp.exemplos,
  requisitos: ['Um usuário do sistema de gestão com permissão de integração.'],
  estado: 'em-breve',
  // O selo já diz "Em breve"; o motivo não promete prazo nem conta da obra.
  motivo: 'Esta integração ainda não está disponível.',
  icone: erp.icone,
  acao: { tipo: 'nenhuma' },
  referencia: erp.chave,
}));

// ── Funcionalidades ─────────────────────────────────────────────────────────

/**
 * Como cada funcionalidade se apresenta na loja.
 *
 * A `descricao` do catálogo é escrita para quem mantém o produto, e às vezes
 * carrega código de task — "(D08 prepara, F04 implementa)". Aqui fica o texto
 * de quem decide adicionar. A funcionalidade que não está nesta lista aparece
 * mesmo assim, com a descrição do catálogo: uma flag nova nunca some da loja
 * por falta de texto.
 */
const APRESENTACAO_DA_FLAG: Readonly<
  Record<
    string,
    {
      nome: string;
      categoria: CategoriaDaLoja;
      linha: string;
      sobre?: string;
      requisitos?: readonly string[];
      icone: NomeDoIcone;
    }
  >
> = {
  resumo_de_ia_ao_encerrar: {
    nome: 'Resumo da conversa',
    categoria: 'ia',
    linha: 'Ao encerrar uma conversa, a IA escreve um resumo e o salva no cliente.',
    sobre:
      'Quando a conversa é encerrada, a IA lê o que foi dito e escreve um resumo curto, salvo como nota no cliente. A nota diz que veio da IA, e quem atende pode revisá-la.',
    icone: 'ia',
  },
  clinica: {
    nome: 'Clínica',
    categoria: 'crm',
    linha: 'A ficha do paciente no cadastro de cada cliente.',
    sobre:
      'Cada cliente ganha uma ficha de paciente: nome social, nascimento, endereço, profissão, estado civil, contato de emergência e a melhor forma e horário de contato. A ficha fica no cadastro do cliente, e quem atende vê tudo no mesmo lugar.',
    icone: 'pessoa',
  },
  automacoes: {
    nome: 'Automações',
    categoria: 'crm',
    linha: 'Regras que agem sozinhas quando algo acontece no CRM.',
    sobre:
      'Libera a área de Automações para gestores e administradores: regras que movem oportunidades, distribuem contatos, criam tarefas e enviam mensagens quando algo acontece. A criação das regras chega a esta área aos poucos.',
    icone: 'automacao',
  },
};

/**
 * Funcionalidades que existem, mas não aparecem na loja.
 *
 * `push_com_conteudo` (ADR-0017): ligá-la em produção espera o Jurídico, e ela
 * não se adiciona por aqui. Na loja seria um item que ninguém pode adicionar.
 * A chave continua valendo no envio do push, desligada por padrão.
 */
const FORA_DA_LOJA: ReadonlySet<string> = new Set(['push_com_conteudo']);

/** `resumo_de_ia_ao_encerrar` → `Resumo de ia ao encerrar`. Só para a flag sem texto. */
function nomeDaChave(chave: string): string {
  const texto = chave.replace(/_/g, ' ');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function itensDeFlags(
  flags: readonly FlagDoTenant[],
  chavesDePortal: ReadonlySet<string>,
): ItemDaLoja[] {
  return (
    flags
      /*
       * A chave de portal já aparece como portal. O prefixo cobre quem não lê
       * o catálogo de portais (a matriz o dá só a quem vê integração): para
       * essa pessoa, a chave de um conector não é recurso do CRM.
       */
      .filter(
        (f) =>
          !chavesDePortal.has(f.chave) &&
          !f.chave.startsWith('conector_') &&
          !FORA_DA_LOJA.has(f.chave),
      )
      .map((flag) => {
        const apresentacao = APRESENTACAO_DA_FLAG[flag.chave];
        /*
         * `permanente` não tem botão, ligada ou não — a regra da tela de
         * antes, mantida. Ligada, "não pode ser removida"; desligada, ela não
         * se adiciona por aqui, e a loja diz isso em vez de oferecer.
         */
        const estado: EstadoNaLoja = flag.ligada
          ? 'adicionada'
          : flag.permanente
            ? 'indisponivel'
            : 'disponivel';

        return {
          id: `flag:${flag.chave}`,
          categoria: apresentacao?.categoria ?? 'crm',
          nome: apresentacao?.nome ?? nomeDaChave(flag.chave),
          linha: apresentacao?.linha ?? flag.descricao,
          sobre: apresentacao?.sobre ?? flag.descricao,
          requisitos: apresentacao?.requisitos ?? [],
          estado,
          ...(estado === 'indisponivel'
            ? { motivo: 'Esta funcionalidade ainda não se adiciona por aqui.' }
            : {}),
          icone: { nome: apresentacao?.icone ?? 'configuracoes' },
          acao: { tipo: 'flag', chave: flag.chave, permanente: flag.permanente },
          referencia: flag.chave,
        };
      })
  );
}

// ── A loja ──────────────────────────────────────────────────────────────────

export interface FontesDaLoja {
  readonly flags: readonly FlagDoTenant[];
  /**
   * `null` quando a pessoa não vê integração: canais, portais e sistemas de
   * gestão ficam de fora da loja dela, pela mesma razão de Conexões sumir do
   * menu.
   */
  readonly integracao: {
    /**
     * `null` enquanto a leitura não chegou, ou se falhou: a categoria fica de
     * fora em vez de dizer "Disponível" para um canal que talvez já esteja
     * conectado — ou "Em breve" para um portal que a organização já tem.
     */
    readonly plataformas: readonly Plataforma[] | null;
    readonly canaisConectados: readonly { tipo: TipoDeCanal }[] | null;
    readonly whatsappConfigurado: boolean;
  } | null;
}

const ORDEM_DO_ESTADO: Readonly<Record<EstadoNaLoja, number>> = {
  adicionada: 0,
  disponivel: 1,
  'em-breve': 2,
  indisponivel: 3,
};

const ORDEM_DA_CATEGORIA: Readonly<Record<CategoriaDaLoja, number>> = {
  canais: 0,
  portais: 1,
  erp: 2,
  crm: 3,
  ia: 4,
};

/**
 * Todos os itens, por categoria e, dentro dela, o que a organização tem
 * primeiro, depois o que pode adicionar, e por último o que ainda não pode.
 *
 * A ordem dos canais dentro do mesmo estado é a do catálogo (do mais usado ao
 * menos) — `sort` é estável.
 */
export function montarLoja(fontes: FontesDaLoja): ItemDaLoja[] {
  const { integracao } = fontes;
  const chavesDePortal = new Set(integracao?.plataformas?.map((p) => p.feature_flag) ?? []);

  const itens = [
    ...(integracao?.canaisConectados
      ? itensDeCanais(integracao.canaisConectados, integracao.whatsappConfigurado)
      : []),
    ...(integracao?.plataformas ? itensDePortais(integracao.plataformas) : []),
    // Não dependem de leitura nenhuma: ficam na loja mesmo se as outras falharem.
    ...(integracao ? ERPS_DA_LOJA : []),
    ...itensDeFlags(fontes.flags, chavesDePortal),
  ];

  return itens.sort(
    (a, b) =>
      ORDEM_DA_CATEGORIA[a.categoria] - ORDEM_DA_CATEGORIA[b.categoria] ||
      ORDEM_DO_ESTADO[a.estado] - ORDEM_DO_ESTADO[b.estado],
  );
}

/** Sem acento e em minúsculas: "transcricao" acha "Transcrição". */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * O recorte da vitrine: a categoria escolhida e a busca.
 *
 * A busca olha o nome, a linha, a categoria, os exemplos e a referência
 * técnica. Os exemplos porque a pessoa procura pelo que quer fazer — "nota
 * fiscal", "estoque" —, e não pelo nome do sistema. A referência porque é o
 * que o suporte dita ao telefone ("procura por `resumo_de_ia`").
 */
export function filtrarLoja(
  itens: readonly ItemDaLoja[],
  categoria: CategoriaDaLoja | null,
  busca: string,
): ItemDaLoja[] {
  const termos = normalizar(busca).split(/\s+/).filter(Boolean);
  const nomeDaCategoria = new Map(CATEGORIAS_DA_LOJA.map((c) => [c.chave, c.nome]));

  return itens.filter((item) => {
    if (categoria !== null && item.categoria !== categoria) return false;
    if (termos.length === 0) return true;
    const texto = normalizar(
      [
        item.nome,
        item.linha,
        nomeDaCategoria.get(item.categoria) ?? '',
        ...(item.exemplos ?? []),
        item.referencia,
      ].join(' '),
    );
    return termos.every((termo) => texto.includes(termo));
  });
}
